import { onBeforeUnmount, onMounted } from 'vue'

/**
 * Reusable parallax initialization
 * Applies to each element with `[data-parallax]`
 *
 * - Initializes on mount, cleans up on unmount
 * - Uses `requestAnimationFrame` to throttle updates and improve performance
 * - Skips initialization if user prefers reduced motion (`prefers-reduced-motion`)
 *
 * Configurable via data attributes:
 *
 * - `data-parallax-speed` (number): scrolling multiplier, default `0.5`
 * - `data-parallax-direction` ('up' | 'down'): scroll direction, default `'up'`
 *
 * Example:
 * `<div data-parallax data-parallax-speed="0.2" data-parallax-direction="down">...</div>`
 */

export const useParallax = () => {
  let handleScroll: () => void
  let ticking: boolean = false

  const initialize = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return // User prefers reduced motion, do not initialize parallax
    }

    const elements = [...document.querySelectorAll('[data-parallax]')].filter(
      (el) => el instanceof HTMLElement
    )

    handleScroll = () => {
      if (ticking) return

      ticking = true
      requestAnimationFrame(() => {
        const viewportHeight = window.innerHeight
        const scrollPosition = window.scrollY
        const documentHeight = document.body.scrollHeight
        const mainViewportDelta = scrollPosition / (documentHeight - viewportHeight)

        elements.forEach((el) => {
          const speed = parseFloat(el.dataset['parallaxSpeed'] || '0.5')
          const direction = el.dataset['parallaxDirection'] || 'up'

          // Calculate relative element offset based on total viewport delta
          const delta = (direction === 'up' ? -1 : 1) * speed * el.offsetHeight * mainViewportDelta

          el.style.transform = `translateY(${delta}px)`
        })
        ticking = false
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll, { passive: true })

    requestAnimationFrame(handleScroll)
  }

  const uninitialize = () => {
    if (handleScroll) {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }

  onMounted(() => {
    initialize()
  })

  onBeforeUnmount(() => {
    uninitialize()
  })

  return { initialize, uninitialize }
}
